import { useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { useAuthStore } from "../../store/authStore";
import Spinner from "../../components/Spinner";

// ── 이름 풀이 타입 ──
type SyllableInfo = {
  char: string;
  stroke_count: number;
  yin_yang: string;
  element: string;
  meaning: string;
};

type AnalysisResult = {
  name: string;
  has_saju: boolean;
  syllables: SyllableInfo[];
  harmony_score: number;
  harmony_summary: string;
  strengths: string[];
  weaknesses: string[];
  overall: string;
};

// ── 작명 타입 ──
type NameSuggestion = {
  name: string;
  given_name: string;
  elements: string[];
  meaning: string;
  harmony: string;
};

type SuggestionResult = {
  surname: string;
  weak_elements: string[];
  suggestions: NameSuggestion[];
  overall_advice: string;
};

const ELEMENT_COLOR: Record<string, string> = {
  목: "bg-green-900/30 text-green-400 border-green-500/30",
  화: "bg-red-900/30 text-red-400 border-red-500/30",
  토: "bg-yellow-900/30 text-yellow-400 border-yellow-500/30",
  금: "bg-slate-700/30 text-slate-300 border-slate-500/30",
  수: "bg-blue-900/30 text-blue-400 border-blue-500/30",
};

function ElementBadge({ element }: { element: string }) {
  const cls = ELEMENT_COLOR[element] ?? "bg-[var(--color-surface)] text-[var(--color-text-tertiary)] border-[var(--color-border-default)]";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${cls}`}>
      {element}
    </span>
  );
}

function HarmonyBar({ score }: { score: number }) {
  const color =
    score >= 80 ? "bg-green-500" : score >= 60 ? "bg-[var(--color-accent)]" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-[var(--color-border-default)] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-sm font-bold text-[var(--color-text-primary)] w-8 text-right">{score}</span>
    </div>
  );
}

export default function NameAnalysis() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [tab, setTab] = useState<"analyze" | "suggest">("analyze");

  // 이름 풀이 state
  const [name, setName] = useState("");
  const [hanja, setHanja] = useState("");
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // 작명 state
  const [surname, setSurname] = useState("");
  const [suggestionResult, setSuggestionResult] = useState<SuggestionResult | null>(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);

  if (!isAuthenticated) {
    navigate("/login");
    return null;
  }

  async function handleAnalyze() {
    if (!name.trim()) { setAnalysisError("이름을 입력해 주세요."); return; }
    setAnalysisError(null);
    setAnalysisResult(null);
    setAnalysisLoading(true);
    try {
      const res = await client.post("/features/name-analysis", {
        name: name.trim(),
        ...(hanja.trim() && { hanja: hanja.trim() }),
      });
      setAnalysisResult(res.data);
    } catch (e: any) {
      setAnalysisError(e?.response?.data?.detail ?? "이름 풀이를 불러올 수 없습니다.");
    } finally {
      setAnalysisLoading(false);
    }
  }

  async function handleSuggest() {
    if (!surname.trim()) { setSuggestionError("성(姓)을 입력해 주세요."); return; }
    setSuggestionError(null);
    setSuggestionResult(null);
    setSuggestionLoading(true);
    try {
      const res = await client.post("/features/name-suggestion", { surname: surname.trim() });
      setSuggestionResult(res.data);
    } catch (e: any) {
      setSuggestionError(e?.response?.data?.detail ?? "작명 결과를 불러올 수 없습니다.");
    } finally {
      setSuggestionLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">✍️ 이름 풀이 / 작명</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">성명학으로 이름의 기운을 분석합니다</p>
          </div>
        </div>

        {/* 탭 */}
        <div className="flex gap-1 bg-[var(--color-surface)] rounded-xl p-1 mb-5">
          {(["analyze", "suggest"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                tab === t
                  ? "bg-[var(--color-bg)] text-[var(--color-accent)] shadow-sm"
                  : "text-[var(--color-text-tertiary)]"
              }`}
            >
              {t === "analyze" ? "이름 풀이" : "작명 추천"}
            </button>
          ))}
        </div>

        {/* ── 이름 풀이 탭 ── */}
        {tab === "analyze" && (
          <div>
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5 mb-4">
              <label className="block text-xs font-semibold text-[var(--color-text-tertiary)] mb-2">이름 입력</label>
              <div className="flex gap-2 mb-3">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
                  placeholder="예: 홍길동"
                  maxLength={10}
                  className="flex-1 border border-[var(--color-border-default)] rounded-xl px-3 py-2 text-sm bg-[var(--color-bg)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-accent)]"
                />
                <button
                  onClick={handleAnalyze}
                  disabled={analysisLoading}
                  className="px-4 py-2 bg-[var(--color-accent)] text-[var(--color-primary)] rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  분석
                </button>
              </div>
              <label className="block text-xs font-semibold text-[var(--color-text-tertiary)] mb-1">
                한자 입력 <span className="font-normal text-[var(--color-text-disabled)]">(선택 · 입력 시 정확한 획수·의미 분석)</span>
              </label>
              <input
                value={hanja}
                onChange={(e) => setHanja(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
                placeholder="예: 洪吉童"
                maxLength={10}
                className="w-full border border-[var(--color-border-default)] rounded-xl px-3 py-2 text-sm bg-[var(--color-bg)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-accent)]"
              />
              {analysisError && <p className="text-[var(--color-error)] text-xs mt-2">{analysisError}</p>}
            </div>

            {analysisLoading && (
              <div className="flex flex-col items-center py-16 gap-3">
                <Spinner size="lg" />
                <p className="text-sm text-[var(--color-text-tertiary)]">럭키가 이름을 살펴보는 중이에요</p>
              </div>
            )}

            {analysisResult && !analysisLoading && (
              <div className="space-y-4">
                {/* 글자별 분석 */}
                <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
                  <h3 className="text-sm font-bold text-[var(--color-accent)] mb-3">🔤 글자별 분석</h3>
                  <div className="flex gap-3 flex-wrap">
                    {analysisResult.syllables.map((s, i) => (
                      <div key={i} className="flex-1 min-w-[80px] bg-[var(--color-bg)] rounded-xl p-3 text-center border border-[var(--color-border-default)]">
                        <p className="text-2xl font-bold text-[var(--color-text-primary)] mb-1">{s.char}</p>
                        <div className="flex justify-center mb-1">
                          <ElementBadge element={s.element} />
                        </div>
                        <p className="text-[10px] text-[var(--color-text-tertiary)]">{s.yin_yang} · {s.stroke_count}획</p>
                        <p className="text-[10px] text-[var(--color-text-secondary)] mt-1 leading-tight">{s.meaning}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 사주 조화 점수 */}
                {analysisResult.has_saju && (
                  <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
                    <h3 className="text-sm font-bold text-[var(--color-accent)] mb-2">⚖️ 사주 조화 점수</h3>
                    <HarmonyBar score={analysisResult.harmony_score} />
                    <p className="text-sm text-[var(--color-text-secondary)] mt-2">{analysisResult.harmony_summary}</p>
                  </div>
                )}

                {/* 장점 / 보완점 */}
                <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-bold text-green-400 mb-2">✅ 장점</h4>
                      <ul className="space-y-1">
                        {analysisResult.strengths.map((s, i) => (
                          <li key={i} className="text-xs text-[var(--color-text-secondary)] leading-relaxed">• {s}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-orange-400 mb-2">💡 보완점</h4>
                      <ul className="space-y-1">
                        {analysisResult.weaknesses.map((w, i) => (
                          <li key={i} className="text-xs text-[var(--color-text-secondary)] leading-relaxed">• {w}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* 종합 분석 */}
                <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
                  <h3 className="text-sm font-bold text-[var(--color-accent)] mb-2">📋 종합 분석</h3>
                  <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-wrap">{analysisResult.overall}</p>
                </div>

                <button
                  onClick={() => { setAnalysisResult(null); setName(""); setHanja(""); }}
                  className="w-full py-2 text-sm text-[var(--color-text-tertiary)] underline"
                >
                  다른 이름 분석하기
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── 작명 탭 ── */}
        {tab === "suggest" && (
          <div>
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5 mb-4">
              <label className="block text-xs font-semibold text-[var(--color-text-tertiary)] mb-1">성(姓) 입력</label>
              <p className="text-[10px] text-[var(--color-text-disabled)] mb-2">성만 입력하면 이름 후보를 추천해 드립니다</p>
              <div className="flex gap-2">
                <input
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSuggest()}
                  placeholder="예: 홍"
                  maxLength={3}
                  className="flex-1 border border-[var(--color-border-default)] rounded-xl px-3 py-2 text-sm bg-[var(--color-bg)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-accent)]"
                />
                <button
                  onClick={handleSuggest}
                  disabled={suggestionLoading}
                  className="px-4 py-2 bg-[var(--color-accent)] text-[var(--color-primary)] rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  추천
                </button>
              </div>
              {suggestionError && <p className="text-[var(--color-error)] text-xs mt-2">{suggestionError}</p>}
            </div>

            {suggestionLoading && (
              <div className="flex flex-col items-center py-16 gap-3">
                <Spinner size="lg" />
                <p className="text-sm text-[var(--color-text-tertiary)]">럭키가 잘 맞는 이름을 골라오는 중이에요</p>
              </div>
            )}

            {suggestionResult && !suggestionLoading && (
              <div className="space-y-4">
                {/* 부족한 오행 */}
                <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] px-5 py-3 flex items-center gap-2">
                  <span className="text-xs text-[var(--color-text-tertiary)]">보완할 오행:</span>
                  {suggestionResult.weak_elements.map((e) => (
                    <ElementBadge key={e} element={e} />
                  ))}
                </div>

                {/* 추천 이름 카드 */}
                {suggestionResult.suggestions.map((s, i) => (
                  <div key={i} className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-[var(--color-text-primary)]">{s.name}</span>
                        <div className="flex gap-1">
                          {s.elements.map((e, j) => <ElementBadge key={j} element={e} />)}
                        </div>
                      </div>
                      <span className="text-xs text-[var(--color-accent)] font-semibold">#{i + 1}</span>
                    </div>
                    <p className="text-sm text-[var(--color-text-secondary)] mb-1 leading-relaxed">{s.meaning}</p>
                    <p className="text-xs text-[var(--color-text-tertiary)] leading-relaxed">⚖️ {s.harmony}</p>
                  </div>
                ))}

                {/* 전반 조언 */}
                <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
                  <h3 className="text-sm font-bold text-[var(--color-accent)] mb-2">💬 작명 조언</h3>
                  <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{suggestionResult.overall_advice}</p>
                </div>

                <button
                  onClick={() => { setSuggestionResult(null); setSurname(""); }}
                  className="w-full py-2 text-sm text-[var(--color-text-tertiary)] underline"
                >
                  다시 추천받기
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
